import { NotificationEntry, WaitlistEntry } from '../../../entities/waitlist';
import { ITimeSlot } from '../../../logic/gymscheduling/igymscheduling';
import { IWaitinglistRepository } from '../interfaces';
import { PouchDbQueryHelper } from './pouchdb-query-helper';

type DbType = any; // todo: add an interface for how the db structure looks

export class DbWaitinglistRepository implements IWaitinglistRepository {
  private db: PouchDbQueryHelper;

  constructor(private waitinglistDb: any, logger?: any | undefined) {
    this.db = new PouchDbQueryHelper(this.waitinglistDb, 'DbWaitinglistRepository', logger);
  }

  public add(waitinglistEntry: WaitlistEntry): Promise<WaitlistEntry> {
    const doc = JSON.parse(JSON.stringify(waitinglistEntry));
    doc._id = waitinglistEntry.uid;
    return this.waitinglistDb.put(doc);
  }

  public async get(waitinglistEntryUid: string): Promise<WaitlistEntry | undefined> {
    try {
      const waitinglistEntry = this.assignToWaitlistEntryObject(await this.waitinglistDb.get(waitinglistEntryUid));
      return waitinglistEntry;
    } catch (err) {
      if (err.status === 404) {
        return undefined;
      }
      throw err;
    }
  }

  public async update(waitinglistEntry: WaitlistEntry): Promise<WaitlistEntry> {
    const doc = JSON.parse(JSON.stringify(waitinglistEntry));
    return this.waitinglistDb.put(doc);
  }

  public async getAllForUser(userId: string): Promise<WaitlistEntry[]> {
    const queryOptions = {
      selector: {
        'userProfile.uid': userId,
      },
    };
    const docs = await this.db.queryAndGetDocs(`getAllForUser(${userId})`, queryOptions);
    const entries = docs.map((entry: DbType) => this.assignToWaitlistEntryObject(entry));
    return entries;
  }

  public async getAllForTimeslot(timeSlot: ITimeSlot): Promise<WaitlistEntry[]> {
    const queryOptions = {
      selector: {
        $and: [
          { 'timeSlot.startTime': timeSlot.startTime },
          { 'timeSlot.endTime': timeSlot.endTime },
          { 'timeSlot.workoutType': timeSlot.workoutType },
        ],
      },
    };
    const unmapped = await this.waitinglistDb.find(queryOptions);
    const entries = unmapped.docs.map((entry: DbType) => this.assignToWaitlistEntryObject(entry));
    return entries;
  }

  public async remove(waitlistEntry: WaitlistEntry): Promise<WaitlistEntry> {
    const entry = await this.get(waitlistEntry.uid);
    return this.waitinglistDb.remove(entry);
  }

  public async getAllEndingInFuture(after: Date): Promise<WaitlistEntry[]> {
    const query = {
      selector: {
        'timeSlot.endTime': {
          $gte: after.getTime(),
        },
      },
      use_index: [
        '_design/endTimeIndex',
        'endTimeIndex',
      ],
      limit: 10000,
      sort: ['timeSlot.endTime'],
    };
    const docs = await this.db.queryAndGetDocs('getAll', query);
    return docs.map((doc: DbType) => this.assignToWaitlistEntryObject(doc));
  }

  private assignToWaitlistEntryObject(entry: DbType): WaitlistEntry {
    const returnObject = Object.assign(new WaitlistEntry(entry.timeSlot, entry.userProfile, entry.gymName), entry);
    returnObject._id = entry._id;
    returnObject._rev = entry._rev;
    this.setDates(returnObject);
    return returnObject;
  }

  private setDates(entry: DbType) {
    entry.timeSlot.startTime = Number(entry.timeSlot.startTime);
    entry.timeSlot.endTime = Number(entry.timeSlot.endTime);
    entry.notifications = entry.notifications.map((noti: NotificationEntry) => {
      noti.date = new Date(noti.date);
      return noti;
    });
  }
}
